<template>
  <sdf-floating-pane
    ref="paneA"
    v-bind="$props"
    @sdfAttemptToClose="$emit('sdf-attempt-to-close', $event)"
    @sdfAfterOpen="onAfterOpen('A', $event)"
    @sdfAfterClose="onAfterClose('A', $event)"
    @sdfAutoClose="$emit('sdf-auto-close', $event)"
  >
    <template v-for="slotName in sdfFloatingPaneSlotNames" :key="slotName">
      <div v-if="$slots[slotName]" :slot="slotName !== 'default' ? slotName : null" class="contents">
        <slot :name="slotName"></slot>
      </div>
    </template>
  </sdf-floating-pane>

  <sdf-floating-pane
    v-if="!props.singleMode"
    ref="paneB"
    v-bind="$props"
    @sdfAttemptToClose="$emit('sdf-attempt-to-close', $event)"
    @sdfAfterOpen="onAfterOpen('B', $event)"
    @sdfAfterClose="onAfterClose('B', $event)"
    @sdfAutoClose="$emit('sdf-auto-close', $event)"
  >
    <template v-for="slotName in sdfFloatingPaneSlotNames" :key="slotName">
      <div v-if="$slots[slotName]" :slot="slotName !== 'default' ? slotName : null" class="contents">
        <slot :name="slotName"></slot>
      </div>
    </template>
  </sdf-floating-pane>
</template>

<script setup lang="ts">
import { sdfFloatingPaneSlotNames, type SdfFloatingPaneSlots } from '@shared/ui/types/slots';
import type { JSX, SdfFloatingPaneCustomEvent } from '@waypoint/web-components';
import { ref, useTemplateRef } from 'vue';

type SdfFloatingPaneElement = HTMLElement &
  JSX.SdfFloatingPane & {
    open: (anchor?: HTMLElement | MouseEvent) => Promise<void>;
    close: () => Promise<void>;
  };

type PaneId = 'A' | 'B';
type Props = Partial<JSX.SdfFloatingPane> & {
  singleMode?: boolean;
};

defineOptions({
  name: 'SdfFloatingPane',
});

const props = withDefaults(defineProps<Props>(), {
  singleMode: false,
  portalEnabled: true,
});
defineSlots<SdfFloatingPaneSlots>();

const emit = defineEmits<{
  'sdf-attempt-to-close': [event: SdfFloatingPaneCustomEvent<EventTarget & HTMLElement>];
  'sdf-after-open': [event: SdfFloatingPaneCustomEvent<void>];
  'sdf-after-close': [event: SdfFloatingPaneCustomEvent<void>];
  'sdf-auto-close': [event: SdfFloatingPaneCustomEvent<void>];
}>();

// Data
const shown = defineModel<boolean>('shown', { default: false });
const paneA = useTemplateRef<SdfFloatingPaneElement>('paneA');
const paneB = useTemplateRef<SdfFloatingPaneElement>('paneB');
const triggerEl = ref<HTMLElement | null>(null);
const activePaneId = ref<PaneId | null>(null);
/**
 * Tracks which pane is being swapped out during a transition.
 *
 * This is needed because sdfAfterOpen/sdfAfterClose events fire after animations
 * complete, not when promises resolve. Without this, the close event from the old
 * pane could emit sdf-after-close before activePaneId is updated, causing parent
 * state to be incorrectly cleared during swaps.
 */
const swappingOutPaneId = ref<PaneId | null>(null);

// Methods
const getPaneEl = (id: PaneId) => {
  return id === 'A' ? paneA.value : paneB.value;
};

const onAfterOpen = (paneId: PaneId, event: SdfFloatingPaneCustomEvent<void>) => {
  activePaneId.value = paneId;
  shown.value = true;
  emit('sdf-after-open', event);
};

const onAfterClose = (paneId: PaneId, event: SdfFloatingPaneCustomEvent<void>) => {
  // Suppressing the close event when swapping out panes
  if (swappingOutPaneId.value === paneId) {
    swappingOutPaneId.value = null;
    return;
  }

  // Only emit if this was the active pane closing
  if (activePaneId.value === paneId) {
    activePaneId.value = null;
    triggerEl.value = null;
    shown.value = false;
    emit('sdf-after-close', event);
  }
};

/**
 * Opens the pane at the given trigger element.
 * If the same trigger is clicked again, it closes the pane.
 * If a different trigger is clicked while open, it swaps to the other pane for smooth animation.
 */
const open = async (anchor: HTMLElement | MouseEvent) => {
  // Get the trigger element from either HTMLElement or MouseEvent
  const newTriggerEl = anchor instanceof MouseEvent ? (anchor.currentTarget as HTMLElement) : anchor;

  if (activePaneId.value && triggerEl.value === newTriggerEl) {
    await close();
    return;
  }

  if (props.singleMode) {
    triggerEl.value = newTriggerEl;
    await paneA.value?.open(newTriggerEl);
    return;
  }

  if (activePaneId.value) {
    // Different trigger - swap to the other pane for smooth animation
    const currentPane = getPaneEl(activePaneId.value);
    const nextPane = getPaneEl(activePaneId.value === 'A' ? 'B' : 'A');

    swappingOutPaneId.value = activePaneId.value;
    triggerEl.value = newTriggerEl;
    await nextPane?.open(newTriggerEl);
    await currentPane?.close();
  } else {
    // No pane open - open pane A
    triggerEl.value = newTriggerEl;
    const pane = getPaneEl('A');
    await pane?.open(newTriggerEl);
  }
};

/**
 * Closes the currently active pane.
 */
const close = async () => {
  if (activePaneId.value) {
    const pane = getPaneEl(activePaneId.value);
    await pane?.close();
  }
};

const getActivePaneElement = () => {
  if (activePaneId.value) {
    return getPaneEl(activePaneId.value) || null;
  }

  return paneA.value || null;
};

defineExpose({
  open,
  close,
  getActivePaneElement,
});
</script>
